import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import useFavorites from '../../../hooks/useFavorites';
import { useCategories } from '../../../hooks/useCategories';

const BookCard = ({ book, priority = false }) => {
  const { toggleFavorite, isProductFavorited, loading } = useFavorites();
  const { categories } = useCategories();
  const [isLiked, setIsLiked] = useState(false);

  useEffect(() => {
    if (book?.id_product) {
      setIsLiked(isProductFavorited(book.id_product, 'book'));
    }
  }, [book?.id_product, isProductFavorited]);

  const getCategoryNames = (categoryIds) => {
    if (!categoryIds || !Array.isArray(categoryIds) || !categories.length) return [];
    return categoryIds
      .map(id => categories.find(cat => cat.id_category === id)?.name_category)
      .filter(Boolean);
  };

  const truncateAuthor = (authorName) => {
    if (!authorName) return 'Autor desconocido';
    return authorName.length > 10 ? `${authorName.substring(0, 10)}...` : authorName;
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating || 0);
    const hasHalfStar = (rating || 0) % 1 >= 0.5;

    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(
          <Icon key={i} name="Star" className="h-2.5 w-2.5 fill-yellow-400 text-yellow-400" />
        );
      } else if (i === fullStars && hasHalfStar) {
        stars.push(
          <Icon key={i} name="Star" className="h-2.5 w-2.5 fill-yellow-400/50 text-yellow-400" />
        );
      } else {
        stars.push(
          <Icon key={i} name="Star" className="h-2.5 w-2.5 text-gray-300" />
        );
      }
    }
    return stars;
  };

  useEffect(() => {
    if (book?.id_product) {
      setIsLiked(isProductFavorited(book.id_product, 'book'));
    }
  }, [book?.id_product, isProductFavorited]);

  const handleLike = async (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (!book?.id_product || loading) return;

    const result = await toggleFavorite(book.id_product, 'book');
    if (result.success) {
      setIsLiked(result.isFavorited);
    }
  };

  if (!book) {
    return (
      <div className="animate-pulse bg-muted rounded-lg p-4 h-full flex flex-col space-y-3">
        <div className="bg-muted-foreground/20 rounded aspect-[3/4] w-full" />
        <div className="h-4 bg-muted-foreground/20 rounded w-3/4" />
        <div className="h-3 bg-muted-foreground/10 rounded w-1/2" />
        <div className="h-3 bg-muted-foreground/10 rounded w-2/3" />
      </div>
    );
  }

  return (
    <Link to={`/product-detail?id=${book.id_product}`} className="group block">
      <div className="bg-card rounded-lg shadow-subtle hover:shadow-modal transition-gentle hover-lift overflow-hidden h-full flex flex-col sm:min-h-[280px] md:min-h-[320px]">
        <div className="relative aspect-[3/4] overflow-hidden flex-shrink-0 flex items-center justify-center"
             style={{
               backgroundImage: 'url(/assets/images/background_image.png)',
               backgroundSize: 'cover',
               backgroundPosition: 'center',
               backgroundRepeat: 'no-repeat'
             }}>
          <div className="relative w-[80px] h-[110px] flex items-center justify-center">
            <Image
              src={book.image_url_product?.[0] || '/placeholder.jpg'}
              alt={`Portada de ${book.title_product}`}
              className="w-full h-full object-cover rounded shadow-lg"
              low
              loading={priority ? 'eager' : 'lazy'}
              fetchPriority={priority ? 'high' : undefined}
              sizes="(min-width: 1280px) 16vw, (min-width: 1024px) 20vw, (min-width: 640px) 25vw, 45vw"
            />
          </div>
          <button
            onClick={handleLike}
            className={`absolute top-2 right-2 flex h-8 w-8 items-center justify-center rounded-full shadow-modal transition-smooth sm:h-9 sm:w-9 ${
              isLiked
                ? 'bg-accent text-accent-foreground'
                : 'bg-white/90 text-muted-foreground hover:bg-white hover:text-accent'
            }`}
            aria-label="Añadir a favoritos"
          >
            <Icon
              name="Heart"
              className={`h-4 w-4 ${isLiked ? 'fill-current' : ''} sm:h-5 sm:w-5`}
            />
          </button>
          <div className="absolute top-2 left-2 flex flex-col gap-1">
            <div className="bg-blue-500 text-white text-xs font-bold px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-full">
              Libro
            </div>
            {book.is_new_product && (
              <div className="bg-success text-success-foreground text-xs font-bold px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-full">
                Nuevo
              </div>
            )}
          </div>
        </div>

        <div className="p-2 sm:p-3 flex-1 flex flex-col justify-between">
          <div className="space-y-1">
            <h3 className="font-bold text-sm sm:text-base leading-tight line-clamp-2 text-foreground group-hover:text-primary transition-colors">
              {book.title_product && book.title_product.length > 17 ? `${book.title_product.substring(0, 17)}...` : book.title_product}
            </h3>
            <p className="text-[10px] sm:text-xs text-muted-foreground line-clamp-1">
              {truncateAuthor(book.authorName)}
            </p>

            {book.rating_product && book.rating_product > 0 && (
              <div className="flex items-center gap-1">
                <div className="flex items-center gap-0.5">
                  {renderStars(book.rating_product)}
                </div>
                <span className="text-[9px] sm:text-[10px] text-muted-foreground">
                  ({book.rating_product.toFixed(1)})
                </span>
              </div>
            )}
          </div>

          <div className="mt-2 space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-xs sm:text-sm font-semibold text-primary">
                ${book.price_original_product?.toFixed(2) || '0.00'}
              </span>
              {book.available_product ? (
                <span className="text-[10px] sm:text-xs text-green-600 font-medium">
                  Disponible
                </span>
              ) : (
                <span className="text-[10px] sm:text-xs text-red-500 font-medium">
                  No disponible
                </span>
              )}
            </div>

            {book.categories_product &&
              Array.isArray(book.categories_product) &&
              book.categories_product.length > 0 && (
                <div className="block mt-1">
                  {(() => {
                    const categoryNames = getCategoryNames(book.categories_product);
                    if (categoryNames.length === 0) return null;

                    const displayCategories = categoryNames.slice(0, 2);
                    const remainingCount = categoryNames.length - 2;

                    return (
                      <div className="flex items-center gap-1 flex-wrap">
                        {displayCategories.map((categoryName, index) => (
                          <span key={index} className="text-[11px] bg-muted text-muted-foreground px-2 py-1 rounded-full">
                            {categoryName}
                          </span>
                        ))}
                        {remainingCount > 0 && (
                          <span className="text-[11px] bg-primary/10 text-primary px-2 py-1 rounded-full font-medium">
                            +{remainingCount}
                          </span>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}
          </div>
        </div>
      </div>
    </Link>
  );
};

export default BookCard;
